import * as THREE from 'three';

// Small landing shuttle — a wide, low hull with a raised cockpit
// canopy, four belly thrusters, and a central belly emitter ring that
// projects a tractor beam. The ship never touches down; it hovers and
// lifts the commander up through the beam.
//
// Returned group's userData exposes:
//   - thrusters:       glow cones, toggled on/off during engine phases
//   - navLight:        top blinking tip light
//   - beamMesh:        the scalable beam cylinder (add to scene separately)
//   - beamMat:         shader material with uTime / uIntensity uniforms
//   - emitterMat:      belly disc material (brightens while beam is on)

export function createRescueShip() {
  const group = new THREE.Group();

  // All ship materials opt out of scene fog so the shuttle reads
  // clearly against any atmosphere — same treatment as distant planets
  // and other celestials. Cloned materials (nav-light/thruster-glow
  // instances) inherit `fog: false` through .clone().
  const hullMat = new THREE.MeshStandardMaterial({
    color: 0xd6dce6, roughness: 0.55, metalness: 0.45, flatShading: true,
    fog: false,
  });
  const accentMat = new THREE.MeshStandardMaterial({
    color: 0xc13f3f, roughness: 0.65, metalness: 0.4, flatShading: true,
    fog: false,
  });
  const trimMat = new THREE.MeshStandardMaterial({
    color: 0x2a303c, roughness: 0.75, metalness: 0.5, flatShading: true,
    fog: false,
  });
  const darkMat = new THREE.MeshStandardMaterial({
    color: 0x1c1e28, roughness: 0.7, metalness: 0.6, flatShading: true,
    fog: false,
  });
  const windowMat = new THREE.MeshStandardMaterial({
    color: 0xa4d4ff, emissive: 0x3070b0, emissiveIntensity: 0.7,
    metalness: 0.8, roughness: 0.2, fog: false,
  });
  const navLightMat = new THREE.MeshStandardMaterial({
    color: 0xff3828, emissive: 0xff3828, emissiveIntensity: 1.6,
    fog: false,
  });
  const glowMat = new THREE.MeshStandardMaterial({
    color: 0xff8850, emissive: 0xff7028, emissiveIntensity: 3.2,
    transparent: true, opacity: 0.85, fog: false,
  });
  const emitterMat = new THREE.MeshStandardMaterial({
    color: 0x9ceaff, emissive: 0x3ec6e8, emissiveIntensity: 0.4,
    roughness: 0.3, fog: false,
  });

  // ---- Main hull — wide flattened disc ----
  const hull = new THREE.Mesh(
    new THREE.CylinderGeometry(1.9, 2.2, 1.0, 20), hullMat,
  );
  hull.position.y = 0.6;
  hull.castShadow = true;
  group.add(hull);

  const belly = new THREE.Mesh(
    new THREE.CylinderGeometry(2.05, 2.3, 0.18, 20), darkMat,
  );
  belly.position.y = 0.1;
  group.add(belly);

  const band = new THREE.Mesh(
    new THREE.CylinderGeometry(2.0, 2.15, 0.22, 20), accentMat,
  );
  band.position.y = 0.75;
  group.add(band);

  const top = new THREE.Mesh(
    new THREE.CylinderGeometry(1.7, 1.9, 0.2, 20), hullMat,
  );
  top.position.y = 1.2;
  group.add(top);

  // ---- Cockpit canopy ----
  const cockpit = new THREE.Mesh(
    new THREE.SphereGeometry(0.75, 20, 14, 0, Math.PI * 2, 0, Math.PI * 0.55),
    hullMat,
  );
  cockpit.position.set(0, 1.25, 0.85);
  cockpit.scale.set(1.0, 0.75, 1.15);
  cockpit.castShadow = true;
  group.add(cockpit);

  const canopy = new THREE.Mesh(
    new THREE.SphereGeometry(0.72, 22, 16, 0, Math.PI * 2, 0, Math.PI * 0.5),
    windowMat,
  );
  canopy.position.set(0, 1.27, 0.85);
  canopy.scale.set(0.95, 0.7, 1.1);
  group.add(canopy);

  const canopyTrim = new THREE.Mesh(
    new THREE.TorusGeometry(0.68, 0.04, 8, 26),
    trimMat,
  );
  canopyTrim.rotation.x = -Math.PI / 2;
  canopyTrim.position.set(0, 1.17, 0.85);
  canopyTrim.scale.set(1.05, 0.78, 1.1);
  group.add(canopyTrim);

  // Wingtip nav lights
  for (const side of [-1, 1]) {
    const nav = new THREE.Mesh(
      new THREE.SphereGeometry(0.08, 10, 8), navLightMat.clone(),
    );
    nav.position.set(side * 2.05, 0.85, -0.9);
    group.add(nav);
  }
  const navLight = new THREE.Mesh(
    new THREE.SphereGeometry(0.09, 10, 8), navLightMat,
  );
  navLight.position.set(0, 1.55, 0.85);
  group.add(navLight);

  // ---- 4 belly thrusters ----
  const thrusters = [];
  for (const a of [0, Math.PI / 2, Math.PI, 3 * Math.PI / 2]) {
    const nozzle = new THREE.Mesh(
      new THREE.CylinderGeometry(0.18, 0.24, 0.3, 12), darkMat,
    );
    nozzle.position.set(Math.cos(a) * 1.5, -0.08, Math.sin(a) * 1.5);
    group.add(nozzle);
    const glow = new THREE.Mesh(
      new THREE.ConeGeometry(0.24, 1.6, 10), glowMat.clone(),
    );
    glow.position.set(Math.cos(a) * 1.5, -0.88, Math.sin(a) * 1.5);
    glow.rotation.x = Math.PI;
    glow.visible = false;
    glow.renderOrder = 2;
    group.add(glow);
    thrusters.push(glow);
  }

  // ---- Belly emitter ring — the beam source ----
  const emitter = new THREE.Mesh(
    new THREE.RingGeometry(0.35, 0.65, 32),
    emitterMat,
  );
  emitter.rotation.x = Math.PI / 2;
  emitter.position.y = -0.02;
  group.add(emitter);

  // Inner disc — brightens when beam is on.
  const emitterCore = new THREE.Mesh(
    new THREE.CircleGeometry(0.35, 28),
    emitterMat,
  );
  emitterCore.rotation.x = Math.PI / 2;
  emitterCore.position.y = -0.01;
  group.add(emitterCore);

  // ---- Tractor beam ----
  // A 1-unit-tall cylinder that the cutscene stretches/positions each
  // frame. Additive, fresnel-rimmed so only the silhouette glows.
  const beamMat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    uniforms: {
      uTime:      { value: 0 },
      uIntensity: { value: 0 },
      uColor:     { value: new THREE.Color(0x9ceaff) },
    },
    vertexShader: /* glsl */`
      varying vec3 vN;
      varying vec3 vV;
      varying float vY;
      void main() {
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vN = normalize(normalMatrix * normal);
        vV = normalize(-mv.xyz);
        // CylinderGeometry y runs -0.5 .. 0.5 on a unit-tall cylinder.
        vY = position.y + 0.5;                   // 0 bottom → 1 top
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */`
      uniform float uTime;
      uniform float uIntensity;
      uniform vec3 uColor;
      varying vec3 vN;
      varying vec3 vV;
      varying float vY;
      void main() {
        float rim = 1.0 - abs(dot(vN, vV));
        float fresnel = pow(rim, 1.2);
        // Brighter near the top (ship side), tapers toward the ground.
        float taper = mix(0.35, 1.0, vY);
        // Gentle scanline flicker rolling down the beam.
        float scan = 0.7 + 0.3 * sin(vY * 18.0 - uTime * 2.2);
        float alpha = fresnel * taper * scan * uIntensity * 0.75;
        gl_FragColor = vec4(uColor, alpha);
      }
    `,
  });
  const beamGeo = new THREE.CylinderGeometry(0.75, 1.05, 1, 28, 1, true);
  const beamMesh = new THREE.Mesh(beamGeo, beamMat);
  beamMesh.visible = false;
  beamMesh.renderOrder = 3;

  group.userData = {
    thrusters, navLight, emitterMat, beamMesh, beamMat,
  };
  return group;
}
